/**
 * Needs review — types something is sending that aren't in the catalog.
 *
 * The label names the decision, not the mechanism. "Held" describes what the
 * server did; "needs review" describes what you have to do. The three outcomes
 * are spelled out at the top because the buttons alone can't carry the
 * difference between dismiss (not now) and block (never).
 */
import { Badge, Button, Card } from '@servicecompressionllc/sc-design-system';
import type { TenantEventType } from '../../lib/types.js';
import { formatAge } from './EventsPage.js';

interface Props {
  held: TenantEventType[];
  onRegister: (entry: TenantEventType) => void;
  onDismiss: (entry: TenantEventType) => void;
  onBlock: (entry: TenantEventType) => void;
  isBusy: boolean;
}

const OUTCOMES = [
  { label: 'Register', tone: 'bg-success', text: 'adds it to the catalog and starts routing it.' },
  {
    label: 'Dismiss',
    tone: 'bg-ink-subtle',
    text: 'clears it for now. It comes back if it arrives again.',
  },
  {
    label: 'Block',
    tone: 'bg-danger',
    text: 'refuses this type at the door and stops listing it.',
  },
];

const FieldList = ({ entry }: { entry: TenantEventType }) => {
  const shape = entry.shape;
  if (!shape || shape.fields.length === 0) {
    return <p className="text-ink-subtle text-xs">No fields recorded yet.</p>;
  }
  return (
    <div className="border-outline overflow-hidden rounded-md border">
      {shape.fields.map((f) => (
        <div
          key={f.name}
          className="border-outline bg-surface flex items-center gap-3 border-b px-3 py-1.5 last:border-b-0"
        >
          <span className="text-ink flex-grow font-mono text-xs">{f.name}</span>
          <span className="text-ink-muted text-xs">{f.type}</span>
        </div>
      ))}
    </div>
  );
};

export const EventReviewTab = ({ held, onRegister, onDismiss, onBlock, isBusy }: Props) => {
  if (held.length === 0) {
    return (
      <Card className="px-6 py-12 text-center">
        <p className="text-ink text-sm font-semibold">Nothing waiting</p>
        <p className="text-ink-muted mx-auto mt-2 max-w-[56ch] text-sm">
          Every type arriving is one you have already decided on. Anything new shows up here.
        </p>
      </Card>
    );
  }

  return (
    <div>
      <p className="text-ink-muted max-w-[74ch] text-sm">
        Something is sending these, but they aren’t in the catalog yet, so they’re held. Nothing ran
        and nothing was thrown away. Each one needs a decision from you.
      </p>

      <div className="mt-3 flex flex-wrap gap-6">
        {OUTCOMES.map((o) => (
          <div key={o.label} className="flex max-w-[26ch] items-start gap-2">
            <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${o.tone}`} />
            <p className="text-ink-muted text-xs">
              <span className="text-ink font-semibold">{o.label}</span> {o.text}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-col gap-3">
        {held.map((entry) => (
          <Card key={entry.id} padding="none" className="overflow-hidden">
            <div className="bg-surface border-outline flex items-center gap-3 border-b px-4 py-3">
              <div className="min-w-0 flex-grow">
                <div className="text-ink font-mono text-sm font-semibold">{entry.type}</div>
                <div className="text-ink-muted mt-0.5 text-xs">
                  {entry.receivedCount} received
                  {entry.firstSeenAt ? ` · first ${formatAge(entry.firstSeenAt)} ago` : ''}
                  {entry.lastSeenAt ? ` · latest ${formatAge(entry.lastSeenAt)} ago` : ''}
                  {/* Who to go talk to — the decision here is usually about a
                      system, not about the type in the abstract. */}
                  {entry.lastSenderName ? ` · from ${entry.lastSenderName}` : ''}
                </div>
              </div>
              <Button variant="secondary" disabled={isBusy} onClick={() => onBlock(entry)}>
                Block
              </Button>
              <Button variant="secondary" disabled={isBusy} onClick={() => onDismiss(entry)}>
                Dismiss
              </Button>
              <Button disabled={isBusy} onClick={() => onRegister(entry)}>
                Register…
              </Button>
            </div>

            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <div>
                <h4 className="text-ink-muted text-xs font-semibold uppercase tracking-wide">
                  Fields we saw
                </h4>
                <p className="text-ink-subtle mb-2 mt-1 text-xs">
                  Read from every payload received. This becomes what workflows can reference.
                </p>
                <FieldList entry={entry} />
              </div>
              <div className="min-w-0">
                {/* Not the latest one: the sample is kept only when a payload
                    brings a new field, so dating it with lastSeenAt read as
                    minutes old over an example from the first event. */}
                <h4 className="text-ink-muted text-xs font-semibold uppercase tracking-wide">
                  A payload we saw
                </h4>
                <p className="text-ink-subtle mb-2 mt-1 text-xs">
                  One this type really sent, saved when its fields last changed.
                </p>
                {entry.lastPayload ? (
                  <pre className="border-outline bg-surface text-ink-muted overflow-x-auto rounded-md border p-3 text-xs">
                    {JSON.stringify(entry.lastPayload, null, 2)}
                  </pre>
                ) : (
                  <Badge variant="neutral">No payload recorded</Badge>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};
